<script setup lang="ts">
import { computed, ref } from "vue";
import { UiText, UiIcon, UiChip, UiStandardButton, UiInfoBox } from "@shopexperts/shared";
import { useExpertPayoutRequests } from "@/app/composables/wallet";
import { formatCurrency } from "@/v2Projects/utils/display";
import { payoutStatusChip, formatPayoutRequestDate } from "./payoutRequestDisplay";
import RequestPayoutDialog from "./RequestPayoutDialog.vue";

const props = defineProps<{
  actingOrgId: number | null | undefined;
  availableBalanceCents: number;
  currency: string | null;
}>();

const actingOrgId = computed(() => props.actingOrgId);

const {
  payoutRequests,
  isLoading,
  error,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  refetch,
} = useExpertPayoutRequests({ actingOrgId, perPage: 20 });

const isEmpty = computed(
  () => !isLoading.value && !error.value && payoutRequests.value.length === 0,
);

const requestDialogOpen = ref(false);
</script>

<template>
  <section class="font-archivo flex flex-col gap-4">
    <div class="flex items-center justify-between gap-3">
      <UiText as="h2" variant="h4" text="Payout requests" class="font-semibold text-se-dark" />
      <UiStandardButton
        variant="primary"
        size="sm"
        label="Request payout"
        @click="requestDialogOpen = true"
      />
    </div>

    <UiInfoBox v-if="error" variant="error" title="Couldn't load payout requests">
      <div class="flex flex-col items-start gap-3">
        <UiText
          as="p"
          variant="override"
          text="Something went wrong while loading your payout requests. Please try again."
          class="text-sm text-se-gray-700"
        />
        <UiStandardButton variant="outline" size="sm" label="Retry" @click="() => refetch()" />
      </div>
    </UiInfoBox>

    <ul v-else-if="isLoading" class="flex flex-col gap-3">
      <li v-for="n in 3" :key="n" class="flex flex-col gap-3 rounded-xl border border-se-gray-200 bg-se-white p-4">
        <div class="flex items-center justify-between">
          <div class="h-4 w-24 animate-pulse rounded bg-se-gray-200" />
          <div class="h-5 w-16 animate-pulse rounded-full bg-se-gray-200" />
        </div>
        <div class="h-3 w-40 animate-pulse rounded bg-se-gray-200" />
      </li>
    </ul>

    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-se-gray-300 bg-se-gray-50 px-6 py-12 text-center"
    >
      <span class="flex h-12 w-12 items-center justify-center rounded-full bg-se-white text-se-gray-500 shadow-sm">
        <UiIcon name="wallet" class="h-6 w-6" />
      </span>
      <UiText as="p" variant="h5" text="No payout requests yet" class="font-semibold text-se-dark" />
      <UiText
        as="p"
        variant="override"
        text="Request a payout of your available balance and our team will process it for you."
        class="max-w-sm text-sm text-se-gray-500"
      />
    </div>

    <template v-else>
      <ul class="flex flex-col gap-3">
        <li
          v-for="request in payoutRequests"
          :key="request.id"
          class="flex flex-col gap-2 rounded-xl border border-se-gray-200 bg-se-white p-4"
        >
          <div class="flex items-center justify-between gap-3">
            <UiText
              as="span"
              variant="h5"
              :text="formatCurrency(request.amount_cents, request.currency)"
              class="font-semibold text-se-dark"
            />
            <UiChip
              :text="payoutStatusChip[request.status].label"
              variant="plain"
              size="xs"
              rounded="full"
              :bg-color-class="payoutStatusChip[request.status].bgClass"
              :text-color-class="payoutStatusChip[request.status].textClass"
            />
          </div>
          <UiText
            as="p"
            variant="override"
            :text="`Requested ${formatPayoutRequestDate(request.created_at)}${request.decided_at ? ` · Decided ${formatPayoutRequestDate(request.decided_at)}` : ''}`"
            class="text-xs text-se-gray-500"
          />
          <UiText
            v-if="request.notes"
            as="p"
            variant="override"
            :text="`Your note: ${request.notes}`"
            class="text-sm text-se-gray-600"
          />
          <UiText
            v-if="request.public_notes"
            as="p"
            variant="override"
            :text="`Team note: ${request.public_notes}`"
            class="text-sm"
            :class="request.status === 'rejected' ? 'text-rose-700' : 'text-se-gray-600'"
          />
        </li>
      </ul>

      <div v-if="hasNextPage" class="flex justify-center">
        <UiStandardButton
          variant="outline"
          size="sm"
          :label="isFetchingNextPage ? 'Loading…' : 'Load more'"
          :disabled="isFetchingNextPage"
          @click="() => fetchNextPage()"
        />
      </div>
    </template>

    <RequestPayoutDialog
      v-if="requestDialogOpen"
      v-model="requestDialogOpen"
      :acting-org-id="actingOrgId"
      :available-balance-cents="availableBalanceCents"
      :currency="currency"
    />
  </section>
</template>
